import moment from "moment";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  setBrandPerformanceImageAttributeFilters,
  setBrandPerformanceOaOFilters,
  setBrandHealthIndexFilters,
  setBuzzTrackingFilters,
  setBrandPerformanceVolumeFilters,
  setBrandSegmentVolumeFilters,
  setBrandMarketShareVolumeFilters,
  setBrandKPITrendFilters,
  setPageName,
  setParameters,
  setSpecificParameter,
  setSalesByVolumeFilters,
  setBrandKPISummaryFilters,
  setBrandPerformanceSovSomFilters,
  setBrandPerformancePurchaseFunnelFilters,
  setBrandPerformanceImageSnapshotFilters,
  SetMonthlyTableforDownloadFilters,
  setYearlyTableForDownloadFilters,
  setQuarterlyTableForDownloadFilters,
  setBrandPerformanceNIMFilters,
  setBrandPerformanceNIMLatestFilters,
  setBrandPerformanceRVFilters,
  setBrandPerformanceTPVAFilters,
  setBrandPerformanceTPFilters,
  setBrandPerformanceTechAwarenessFilters,
} from "../../actions";
import { brandPerformanceApiParamsToObject } from "../../api/brandPerformanceApi";
import "../../assets/styles/pages/brandPerformanceDashboard.scss";
import UsedCarsGranularityOptions from "../../components/Charts/components/UsedCarsGranularityOptions";
import { getGranularitiesToEnable } from "../../components/Charts/helpers/helpers";
import BrandPerformanceFilters from "../../components/Filters/BrandPerformanceDashboard/BrandPerformanceFilters";
import { DashboardLayout } from "../../components/Layouts";
import { DefaultPageContent } from "../../components/PageContent";
import { PageTitle } from "../../components/PageContent/subs";
import { ALL_OPTION_NO_SPACE, DEFAULT_BRAND_PERFORMANCE_PARAMETERS, __prod__ } from "../../constants";

// SECTION COMPONENTS
import BrandImageAttributes from "./components/BrandImageAttributes/BrandImageAttributes";
import BrandOaO from "./components/BrandOaO/BrandOaO";
import BrandHealthIndex from "./components/BrandHealthIndex/BrandHealthIndex";
import BuzzTracking from "./components/BuzzTracking/BuzzTracking";
import BrandVolume from "./components/BrandVolume/BrandVolume";
import PerfNavButtons from "./components/PerfNavButtons/PerfNavButtons";
import SegmentVolume from "./components/SegmentVolumeTIV/SegmentVolume";
import BrandMarketContent from "./components/BrandMarketShare/BrandMarketContent";
import ShareByChannelContent from "./components/MarketShareByChannel/ShareByChannelContent";
import TPContent from "./components/TP/TPContent";
import LoadingEllipsis from "../../components/Loading/LoadingEllipsis";
import BrandKPIContent from "./components/BrandKPITrend/BrandKPIContent";
import BrandKPISummaryContent from "./components/BrandKPISummary/BrandKPISummaryContent";
import PurchaseFunnelContent from "./components/PurchaseFunnel/PurchaseFunnelContent";
import NIMLatestContent from "./components/NIMLatest/NIMLatestContent";
import TableForDownloadMonthly from "./components/TableDownloads/TableForDownloadMonthly";
import TPVAContent from "./components/TPVA/TPVAContent";
import TechAwarenessContent from "./components/TechAwareness/TechAwarenessContent";
import NIMContent from "./components/NIM/NIMContent";
import SovSomContent from "./components/SovSom/SovSomContent";
import RvContent from "./components/RV/RvContent";
import ImageSnapshotContent from "./components/ImageSnapshot/ImageSnapshotContent";
import TableForDownloadYearly from "./components/TableDownloads/TableForDownloadYearly";
import TableForDownloadQuarterly from "./components/TableDownloads/TableForDownloadQuarterly";
import TableForDownloadRV from "./components/TableDownloads/TableForDownloadRV";

// api calls
import {
  getBrandPerformanceLastRefreshDate,
  getBrandPeformanceBrandKPITrendLastRefreshDate,
  getBrandPeformancePurchaseFunnelLastRefreshDate,
  getBrandPeformanceTechAwarenessLastRefreshDate,
  getBrandPerformanceVolumeFilters,
  getBrandPerformanceOaOFilters,
  getBrandHealthIndexFilters,
  getBuzzTrackingFilters,
  getBrandPerformanceImageAttributesFilters,
  getBrandSegmentVolumeFilters,
  getBrandMarketShareFilters,
  getMarketShareByChannelFilters,
  getBrandKPITrendFilters,
  getBrandKPISummaryFilters,
  getBrandPerformanceSovSomFilters,
  getPurchaseFunnelFilters,
  getImageSnapshotFilters,
  getMonthlyTableForDownloadFilters,
  getYearlyTableForDownloadFilters,
  getQuarterlyTableForDownloadFilters,
  getNIMFilters,
  getNIMLatestFilters,
  getBrandPerformanceAvailableDataDateRange,
  getBrandPerformanceKPITrendAvailableDataDateRange,
  getBrandPerformancePurchaseFunnelAvailableDataDateRange,
  getBrandPerformanceTechAwarenessAvailableDataDateRange,
  getRVFilters,
  getRVTableForDownloadFilters,
  getBrandPerformanceTPVAFilters,
  getBrandPerformanceTPFilters,
  getTechAwarenessFilters,
} from "../../api";

// CUSTOM HOOK
import useFilterQuery from "../../hooks/react-query/brandPerformance/useFilterQuery";
import { pages, pageTooltipText, performancePages } from "../../constants/brandPerformanceConstants";

// create a custom csv url object with the keys being the performance pages and the values being the api endpoints
import { eventTracking, MixpanelEvents } from "../../utils/userTracking";
import { DashboardBannerContext } from "../../context";

// Constants
const YEARLY_MARKETS = ["United States", "United Kingdom", "Germany", "France", "Spain", "Italy"];
const EUR_MARKETS = ["France", "Italy", "Germany", "Spain", "United Kingdom", "G5"];
const PERFORMANCE_DATA_EXCLUSION = ["segment-volume-tiv"];
const PAGES_WITH_DATA_AVAILABLE = [
  "volume",
  // "volume-by-channel",
  "oao",
  "brand-health-index",
  "image-attributes",
  "buzz-tracking",
  "tp",
  "tpva",
  "tech-awareness",
  "brand-kpi-trend",
  "brand-kpi-summary",
  "purchase-funnel",
  "nim",
  "nim-latest",
  "monthly-table-for-download",
  "brand-market-share",
  "image-snapshot",
  "quarterly-table-for-download",
  "market-share-by-channel",
  "segment-volume-tiv",
  "sov/som",
];

const BrandPerformanceDashboard = withRouter((props: RouteComponentProps) => {
  const dispatch = useDispatch();
  const params = useSelector((state: RootStateOrAny) => state.parameters);
  const {
    date_range,
    granularity,
    nissan_competitor: nissanCompetitorParam,
    brand,
    competitor,
    category,
    sub_category,
    model,
    market,
    attribute,
    date_range_brand_perf,
    performance_page: performancePageParam,
    segment,
    brand_kpi,
    data_category,
    ranking,
    select_value,
    data_source: dataSourceParam,
  } = useSelector((state: RootStateOrAny) => state.parameters);
  const { setBannerText, setShowBanner } = useContext(DashboardBannerContext);

  // const lastDataRefresh = useSelector((state: RootStateOrAny) => state.data_refresh.brand_performance_last_data_refresh);
  const [nissan_competitor, setNissanCompetitor] = useState<string>("nissan_competitor");
  const [performancePage, setPerformancePage] = useState<performancePages>("volume");
  const [kpiTrendMaxScoreRange, setKPITrendMaxScoreRange] = useState<number>(100);
  const [nimLatestRanking, setNimLatestRanking] = useState<number>(10);
  const [availableDataDateRange, setAvailableDataDateRange] = useState<{ min_date: string; max_date: string }>({
    min_date: "",
    max_date: "",
  });
  const [shouldRenderDataComponents, setShouldRenderDataComponents] = useState(false);

  // useEffect(() => {
  //   dispatch(setParameters(DEFAULT_BRAND_PERFORMANCE_PARAMETERS));
  // }, []);

  // set banner on performance page in PAGES_WITH_DATA_AVAILABLE
  useEffect(() => {
    const isNissanOrInfinitiUS = (brand === "Nissan" || brand === "Infiniti") && (market === "United States" || market?.toLowerCase() === "infiniti_usa");
    const hasEurMarket = market?.split(",").some((m: string) => EUR_MARKETS.includes(m.trim()));
    // EUR FY25 methodology-change banners are not yet signed off for prod. Gate them to
    // non-prod environments; remove this flag once approved for prod.
    const eurBannerEnabled = !__prod__;

    if (performancePage === "oao") {
      if (isNissanOrInfinitiUS) {
        setBannerText(
          "No new OaO data is available for US from FY25 onwards, please check Brand Health Index (BHI) for the latest brand-related tracking data"
        );
        setShowBanner(true);
      } else if (eurBannerEnabled && hasEurMarket) {
        setBannerText("For EUR G5, OaO before and after FY25 is not comparable due to methodology change");
        setShowBanner(true);
      } else {
        setShowBanner(false);
      }
    } else if (performancePage === "image-attributes") {
      if (isNissanOrInfinitiUS && dataSourceParam === "Gfk") {
        setBannerText('In this GfK Image Report data, it is up to FY24. For the latest data please select "YouGov" from the Data Source');
        setShowBanner(true);
      } else if (eurBannerEnabled && hasEurMarket) {
        setBannerText(
          "For EUR G5, Image data before and after FY25 is not comparable due to methodology change. Image Attribute names shown in this dashboard use the latest attribute names, which may differ from previous reports."
        );
        setShowBanner(true);
      } else {
        setShowBanner(false);
      }
    } else if (performancePage === "image-snapshot") {
      if (isNissanOrInfinitiUS && dataSourceParam === "Gfk") {
        setBannerText('In this GfK Image Snapshot data, it is up to FY24. For the latest data please select "YouGov" from the Data Source');
        setShowBanner(true);
      } else {
        setShowBanner(false);
      }
    } else if (performancePage === "purchase-funnel") {
      if (isNissanOrInfinitiUS && dataSourceParam === "Gfk") {
        setBannerText('In this GfK Purchase Funnel data, it is up to FY24. For the latest data please select "YouGov" from the Data Source');
        setShowBanner(true);
      } else if (eurBannerEnabled && hasEurMarket) {
        setBannerText("For EUR, PF before and after FY25 is not comparable due to methodology change");
        setShowBanner(true);
      } else {
        setShowBanner(false);
      }
    } else if (performancePage === "quarterly-table-for-download") {
      if (isNissanOrInfinitiUS && dataSourceParam === "Gfk") {
        setBannerText('In this GfK data dashboard, it is up to FY24. For the latest data please select "YouGov" from the Data Source');
        setShowBanner(true);
      } else if (eurBannerEnabled && (hasEurMarket || market === "All")) {
        setBannerText("For EUR, data before and after FY25 is not comparable due to methodology change");
        setShowBanner(true);
      } else {
        setShowBanner(false);
      }
    } else {
      setShowBanner(false);
    }
  }, [performancePage, dataSourceParam, brand, market]);

  const customCSVUrl: { [key in performancePages]: string } = {
    "monthly-table-for-download": `/brand-performance/monthly-table-for-download/monthly-table-for-download-csv-download?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    "yearly-table-for-download": `/brand-performance/yearly-table-for-download/yearly-table-for-download-csv-download?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    "quarterly-table-for-download": `/brand-performance/quarterly-table-for-download/quarterly-table-for-download-csv-download?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    "rv-table-for-download": `/brand-performance/rv-table-for-download/csv?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    "nim-latest": `/brand-performance/nim-latest/nim-latest-csv-download?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    nim: `/brand-performance/nim/nim-trend-csv-download?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"}`,
    "purchase-funnel": `/brand-performance/purchase-funnel/nissan-purchase-funnel-data-csv-download`,
    // Not yet implemented on the backend, same as this tab's filters/data endpoints - see
    // getTechAwarenessFilters' comment in api/filtersApi.ts.
    "tech-awareness": `/brand-performance/tech-awareness/tech-awareness-csv-download`,
    "brand-kpi-trend": `/brand-performance/brand-kpi-trend/brand-kpi-trend-csv-download?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    "brand-kpi-summary": `/brand-performance/brand-kpi-summary/brand-kpi-summary-csv-download `,
    "sov/som": `/brand-performance/sov-som/sov-som-trend-csv-download`,
    tpva: `/brand-performance/tpva/tpva-trend-csv-download`,
    tp: `/brand-performance/tp/tp-trend-csv-download`,
    "market-share-by-channel": `/brand-performance/market-share-by-channel/csv?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    "brand-market-share": `/brand-performance/brand-market-share/brand-market-share-trend-csv-download?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    "segment-volume-tiv": `/brand-performance/segment-volume-and-segment-tiv-share-volume/segment-volume-and-segment-tiv-share-volume-trend-csv-download`,
    "image-attributes": `/brand-performance/brand-image-attributes/brand-image-attributes-trend-csv-download?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    oao: `/brand-performance/brand-oao/brand-oao-trend-csv-download?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    "brand-health-index": `/brand-performance/brand-health-index/brand-health-index-trend-csv-download?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    "buzz-tracking": `/brand-performance/buzz-tracking/buzz-tracking-trend-csv-download?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    volume: `/brand-performance/brand-volume/brand-volume-trend-csv-download?nissan_competitor=${nissan_competitor == "nissan_competitor" ? "true" : "false"
      }`,
    "image-snapshot": `/brand-performance/brand-image-snapshot/brand-image-snapshot-csv-download`,
    rv: `/brand-performance/residaul-value/csv`,
  };

  // ANALYSIS_REGISTRY["brand_performance"]'s own page/export keys per tab (services/analysis/
  // registry.py) - read directly from source, not inferred from these tab keys' own naming (e.g.
  // "image-attributes" here is registered as "brand_image", not "brand_image_attributes").
  // "yearly-table-for-download"/"rv-table-for-download" are omitted - no registry page exists for
  // either yet, so DefaultPageContent.tsx falls back to customCSVUrl above for just those two.
  const customCSVExport: Partial<Record<performancePages, { page: string; export: string }>> = {
    "monthly-table-for-download": { page: "table_for_download_monthly", export: "table" },
    "quarterly-table-for-download": { page: "table_for_download_quarterly", export: "table" },
    "nim-latest": { page: "nim_latest", export: "latest" },
    nim: { page: "nim", export: "trend" },
    "purchase-funnel": { page: "purchase_funnel", export: "funnel_data" },
    "brand-kpi-trend": { page: "brand_kpi_trend", export: "trend" },
    "brand-kpi-summary": { page: "brand_kpi_summary", export: "summary" },
    "sov/som": { page: "sov_som", export: "trend" },
    tpva: { page: "tpva", export: "trend" },
    tp: { page: "tp", export: "trend" },
    "market-share-by-channel": { page: "market_share_by_channel", export: "trend" },
    "brand-market-share": { page: "brand_market_share", export: "trend" },
    "segment-volume-tiv": { page: "segment_volume_and_segment_tiv_share_volume", export: "trend" },
    "image-attributes": { page: "brand_image", export: "trend" },
    oao: { page: "brand_oao", export: "trend" },
    "brand-health-index": { page: "brand_health_index", export: "trend" },
    "buzz-tracking": { page: "buzz_tracking", export: "trend" },
    volume: { page: "brand_volume", export: "trend" },
    "image-snapshot": { page: "brand_image_snapshot", export: "snapshot" },
    rv: { page: "residual_value", export: "data" },
  };

  useEffect(() => {
    switch (performancePage) {
      case "image-attributes":
      case "buzz-tracking":
      case "oao":
      case "brand-health-index":
        dispatch(setParameters({ ...params, competitor: ALL_OPTION_NO_SPACE, granularity: "quarterly" }));
        setChartGranularity("quarterly");
        break;
      case "segment-volume-tiv":
      case "brand-market-share":
      case "tp":
      case "tpva":
      case "sov/som":
        dispatch(setParameters({ ...params, competitor: ALL_OPTION_NO_SPACE, date_range: "Last 12 months", granularity: "monthly" }));
        setChartGranularity("monthly");
        break;
      case "monthly-table-for-download":
        dispatch(
          setParameters({
            ...params,
            granularity: "monthly",
            data_category: "Volume",
            competitor: ALL_OPTION_NO_SPACE,
          })
        );
        setChartGranularity("monthly");
        break;
      case "yearly-table-for-download":
        dispatch(
          setParameters({
            ...params,
            granularity: "yearly",
            data_category: "Volume",
            competitor: ALL_OPTION_NO_SPACE,
          })
        );
        setChartGranularity("yearly");
        break;
      case "quarterly-table-for-download":
        dispatch(
          setParameters({
            ...params,
            granularity: "quarterly",
            data_category: "OaO",
            competitor: ALL_OPTION_NO_SPACE,
          })
        );
        setChartGranularity("quarterly");
        break;
      case "rv-table-for-download":
        dispatch(setParameters({ ...params, competitor: ALL_OPTION_NO_SPACE, granularity: "monthly" }));
        setChartGranularity("monthly");
        break;
      case "image-snapshot":
        dispatch(
          setParameters({
            ...params,
            attribute: `${ALL_OPTION_NO_SPACE}`,
            competitor: ALL_OPTION_NO_SPACE,
          })
        );
        setChartGranularity("quarterly");
        break;
      case "nim-latest":
        dispatch(
          setParameters({
            ...params,
            category: `${ALL_OPTION_NO_SPACE}`,
            attribute: `${ALL_OPTION_NO_SPACE}`,
            market: ALL_OPTION_NO_SPACE,
            date_range: "Last 12 months",
          })
        );
        break;
      case "nim":
        dispatch(
          setParameters({
            ...params,
            category: `${ALL_OPTION_NO_SPACE}`,
            attribute: `${ALL_OPTION_NO_SPACE}`,
            sub_category: `${ALL_OPTION_NO_SPACE}`,
            competitor: ALL_OPTION_NO_SPACE,
          })
        );
        break;

      case "rv":
        dispatch(
          setParameters({
            ...params,
            date_range: "Last 12 months",
            category: `${ALL_OPTION_NO_SPACE}`,
            attribute: `${ALL_OPTION_NO_SPACE}`,
            sub_category: `${ALL_OPTION_NO_SPACE}`,
          })
        );
        break;

      case "brand-kpi-summary":
        dispatch(
          setParameters({
            ...params,
            date_range: "Last financial year",
            granularity: "quarterly",
            competitor: ALL_OPTION_NO_SPACE,
          })
        );
        setChartGranularity("quarterly");
        break;

      case "purchase-funnel":
        dispatch(setParameters({ ...params, competitor: "" }));

        break;

      default:
        dispatch(setParameters(DEFAULT_BRAND_PERFORMANCE_PARAMETERS));
        setChartGranularity("monthly");

        break;
    }
  }, [performancePage]);

  const [chartGranularity, setChartGranularity] = useState(granularity);
  const [granularitiesList, setGranularityList] = useState(["monthly", "yearly"]);
  const [diff, setDiff] = useState(0);
  const [onSettleCount, setOnSettleCount] = useState(0);
  const [latestDataRefresh, setLatestDataRefresh] = useState<string>("");

  // fetch last data refresh date
  const { data: lastDataRefresh, refetch: refetchLastDataRefresh } = useQuery(
    ["brandPerformanceLastDataRefresh"],
    () => getBrandPerformanceLastRefreshDate(performancePage, market),
    {
      enabled: false,
    }
  );

  // fetch latest date refresh for purchase funnel
  const { data: lastDataRefreshPurchaseFunnel, refetch: refetchLastDataRefreshPurchaseFunnel } = useQuery(
    ["brandPeformancePurchaseFunnelLastRefreshDate"],
    () => getBrandPeformancePurchaseFunnelLastRefreshDate(market),
    {
      enabled: false,
    }
  );

  // fetch latest date refresh for brand kpi trend
  const { data: lastDataRefreshBrandKPITrend, refetch: refetchLastDataRefreshBrandKPITrend } = useQuery(
    ["brandPeformanceBrandKPITrendLastRefreshDate"],
    () => getBrandPeformanceBrandKPITrendLastRefreshDate(market),
    {
      enabled: false,
    }
  );

  // fetch latest date refresh for tech awareness - dedicated backend endpoint (NS-968)
  const { data: lastDataRefreshTechAwareness, refetch: refetchLastDataRefreshTechAwareness } = useQuery(
    ["brandPeformanceTechAwarenessLastRefreshDate"],
    () => getBrandPeformanceTechAwarenessLastRefreshDate(market),
    {
      enabled: false,
    }
  );

  // fetch available data date range for tech awareness - dedicated backend endpoint (NS-968,
  // cedar-backend commit 856018d1 - landed after this page's own refresh-date wiring, which is
  // why it was a separate follow-up rather than done alongside it).
  const { data: availableDataDateRangeDataTechAwareness, refetch: refetchAvailableDataDateRangeTechAwareness } = useQuery(
    ["brandPerformanceAvailableDataDateRangeTechAwareness"],
    () => getBrandPerformanceTechAwarenessAvailableDataDateRange(params, market),
    {
      enabled: false,
    }
  );

  // fetch available data date range
  const { data: availableDataDateRangeData, refetch: refetchAvailableDataDateRange } = useQuery(
    ["brandPerformanceAvailableDataDateRange"],
    () => getBrandPerformanceAvailableDataDateRange(params, performancePage, market),
    {
      enabled: false,
    }
  );

  // fetch available data date range (table downloads) with 500 handling
  const { data: availableDataDateRangeDataTableForDownload, refetch: refetchAvailableDataDateRangeTableForDownload } = useQuery(
    ["brandPerformanceAvailableDataDateRangeTableForDownload"],
    async () => {
      try {
        return await getBrandPerformanceAvailableDataDateRange(params, data_category, market);
      } catch (err: any) {
        if (err?.response?.status === 500 || err?.status === 500) {
          return { available_data_date_range: { min_date: "", max_date: "" } };
        }
        // Re-throw other errors so react-query can mark the query as errored
        throw err;
      }
    },
    {
      enabled: false,
    }
  );

  // fetch last data refresh date (table downloads) - the same shared /brand-performance/
  // latest-refresh-date endpoint the "default" case below already uses, keyed by data_category
  // (e.g. "Volume"/"OaO"/"Image"/"NIM" - confirmed real, recognized metric values backend-side)
  // rather than performancePage, matching how availableDataDateRangeDataTableForDownload right
  // above already calls its own endpoint. Previously this was never fetched at all - the display
  // effect below just hardcoded an empty string for these 3 pages - so "Last data refresh" showed
  // blank unconditionally even though this same endpoint genuinely has a date for them.
  const { data: lastDataRefreshTableForDownload, refetch: refetchLastDataRefreshTableForDownload } = useQuery(
    ["brandPerformanceLastDataRefreshTableForDownload"],
    () => getBrandPerformanceLastRefreshDate(data_category, market),
    {
      enabled: false,
    }
  );

  // fetch available data date range for kpi trend
  const { data: availableDataDateRangeDataKPITrend, refetch: refetchAvailableDataDateRangeKPITrend } = useQuery(
    ["brandPerformanceAvailableDataDateRangeKPITrend"],
    () => getBrandPerformanceKPITrendAvailableDataDateRange(params, "brand-kpi-trend", market),
    {
      enabled: false,
    }
  );

  // fetch available data date range for purchase funnel
  const { data: availableDataDateRangeDataPurchaseFunnel, refetch: refetchAvailableDataDateRangePurchaseFunnel } = useQuery(
    ["brandPerformanceAvailableDataDateRangePurchaseFunnel"],
    () => getBrandPerformancePurchaseFunnelAvailableDataDateRange(params, "purchase-funnel", market),
    {
      enabled: false,
    }
  );

  // call tech awareness filters - moved up here (ahead of this file's other filter queries, which
  // stay in their original spot below) so the refetch-triggering effect just below can read
  // techAwarenessFilters.market without a "used before declaration" error; nothing else about this
  // query changed.
  const {
    data: techAwarenessFilters,
    isFetching: isLoadingTechAwarenessFilters,
    refetch: refetchTechAwarenessFilters,
  } = useFilterQuery<any, any>(
    ["techAwarenessFilters", chartGranularity, params, nissan_competitor],
    getTechAwarenessFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // performancePage is intentionally in the dependency list below even though it's only ever read
  // inside the switch: without it, switching tabs doesn't by itself re-run this effect - it only
  // reruns when one of the *data objects change reference, and react-query's structural sharing
  // (on by default) keeps the same reference when a refetch resolves to a deeply-equal value as
  // last time (e.g. the same market's tech-awareness refresh date/available range as an earlier
  // visit this session). That left last refresh/available dates stuck showing a previous tab's
  // value (or blank) after switching to Tech Awareness, despite its own endpoints having genuinely
  // returned data - the effect just never re-ran to read it.
  useEffect(() => {
    switch (performancePage) {
      case "brand-kpi-trend":
        setLatestDataRefresh(lastDataRefreshBrandKPITrend?.date ?? "");
        setAvailableDataDateRange({
          min_date: availableDataDateRangeDataKPITrend?.available_data_date_range?.min_date ?? "",
          max_date: availableDataDateRangeDataKPITrend?.available_data_date_range?.max_date ?? "",
        });
        break;
      case "purchase-funnel":
        setLatestDataRefresh(lastDataRefreshPurchaseFunnel?.date ?? "");
        setAvailableDataDateRange({
          min_date: availableDataDateRangeDataPurchaseFunnel?.available_data_date_range?.min_date ?? "",
          max_date: availableDataDateRangeDataPurchaseFunnel?.available_data_date_range?.max_date ?? "",
        });

        break;
      case "monthly-table-for-download":
      case "yearly-table-for-download":
      case "quarterly-table-for-download":
        setLatestDataRefresh(lastDataRefreshTableForDownload?.date ?? "");
        setAvailableDataDateRange({
          min_date: availableDataDateRangeDataTableForDownload?.available_data_date_range?.min_date ?? "",
          max_date: availableDataDateRangeDataTableForDownload?.available_data_date_range?.max_date ?? "",
        });
        break;
      case "tech-awareness":
        setLatestDataRefresh(lastDataRefreshTechAwareness?.date ?? "");
        setAvailableDataDateRange({
          min_date: availableDataDateRangeDataTechAwareness?.available_data_date_range?.min_date ?? "",
          max_date: availableDataDateRangeDataTechAwareness?.available_data_date_range?.max_date ?? "",
        });
        break;
      default:
        setLatestDataRefresh(lastDataRefresh?.date ?? "");
        setAvailableDataDateRange({
          min_date: availableDataDateRangeData?.available_data_date_range?.min_date ?? "",
          max_date: availableDataDateRangeData?.available_data_date_range?.max_date ?? "",
        });

        break;
    }
  }, [
    performancePage,
    lastDataRefresh,
    lastDataRefreshPurchaseFunnel,
    lastDataRefreshBrandKPITrend,
    lastDataRefreshTechAwareness,
    lastDataRefreshTableForDownload,
    availableDataDateRangeData,
    availableDataDateRangeDataKPITrend,
    availableDataDateRangeDataPurchaseFunnel,
    availableDataDateRangeDataTableForDownload,
    availableDataDateRangeDataTechAwareness,
  ]);

  useEffect(() => {
    let list: string[] = [];

    if (date_range?.includes("-")) {
      const diff = moment(date_range?.split("-")[1], "MMM YYYY")
        .endOf("month")
        .diff(moment(date_range?.split("-")[0], "MMM YYYY").startOf("month"), "days");
      setDiff(diff);
      const toEnable = getGranularitiesToEnable(diff, "brand-performance");
      list = toEnable;
    } else if (
      performancePage == "oao" ||
      performancePage == "brand-health-index" ||
      performancePage == "image-attributes" ||
      performancePage == "buzz-tracking" ||
      performancePage == "image-snapshot" ||
      performancePage == "brand-kpi-trend" ||
      performancePage == "purchase-funnel" ||
      performancePage == "tech-awareness" ||
      performancePage == "nim"
      // performancePage == "nim-latest"
    ) {
      const param = brandPerformanceApiParamsToObject(window.location.search);
      //@ts-ignore
      const diff = moment(param?.end_date, "YYYY-MM-DD").diff(moment(param?.start_date, "YYYY-MM-DD"), "days");
      const toEnable = getGranularitiesToEnable(diff, "brand-performance");
      list = toEnable?.filter((val) => val !== "monthly");
      list = list?.length == 0 ? ["quarterly"] : list;
      setDiff(diff);
    }
    // else if (
    //   //   performancePage == "segment-volume-tiv" ||
    //   //   performancePage == "brand-market-share" ||
    //   performancePage == "market-share-by-channel"
    //   //   performancePage == "tp" ||
    //   //   performancePage == "brand-kpi-summary" ||
    //   //   performancePage == "tpva" ||
    //   //   performancePage == "sov/som" ||
    //   //   performancePage == "rv" ||
    // ) {
    //   const param = brandPerformanceApiParamsToObject(window.location.search);
    //   //@ts-ignore
    //   const diff = moment(param?.end_date, "YYYY-MM-DD").diff(moment(param?.start_date, "YYYY-MM-DD"), "days");
    //   const toEnable = getGranularitiesToEnable(diff, "brand-performance");
    //   list = toEnable;
    //   list = toEnable?.length == 0 ? ["quarterly"] : list;
    //   setDiff(diff);
    // }
    else if (
      performancePage == "monthly-table-for-download" ||
      performancePage == "yearly-table-for-download" ||
      performancePage == "quarterly-table-for-download" ||
      performancePage == "rv-table-for-download"
    ) {
      list = ["monthly", "quarterly", "yearly"];
    } else if (performancePage == "nim-latest") {
      list = ["quarterly", "yearly"];
    } else {
      date_range?.includes("month")
        ? (list = ["monthly"])
        : date_range?.includes("quarter")
          ? (list = ["monthly"])
          : (list = ["monthly", "quarterly", "yearly"]);
    }

    // check if the market is in the yearly markets
    if (YEARLY_MARKETS.includes(market)) {
      list = [...list, "yearly"];
    }

    setGranularityList(list);
  }, [date_range, performancePageParam, date_range_brand_perf, performancePage, market]);

  const handleGranularityRadioChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value },
      } = evt;

      setChartGranularity(value);
    },
    [setChartGranularity]
  );

  const handleRadioChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value },
      } = evt;

      setNissanCompetitor(value);
      dispatch(setSpecificParameter("nissan_competitor", value));
    },
    [nissanCompetitorParam, setNissanCompetitor]
  );

  useEffect(() => {
    const pageName = pages.find((page) => page.pageId == performancePage);
    const title = performancePage === "buzz-tracking" ? "Buzz / ATTN / Ad AWA" : (pageName?.pageName ?? "");
    Promise.all([
      dispatch(setPageName(title)),
    ]);
  }, [dispatch, performancePage]);

  useEffect(() => {
    switch (performancePage) {
      case "brand-kpi-trend":
        refetchLastDataRefreshBrandKPITrend();
        refetchAvailableDataDateRangeKPITrend();

        break;
      case "purchase-funnel":
        refetchLastDataRefreshPurchaseFunnel();
        refetchAvailableDataDateRangePurchaseFunnel();

        break;

      case "monthly-table-for-download":
      case "yearly-table-for-download":
      case "quarterly-table-for-download":
        refetchAvailableDataDateRangeTableForDownload();
        refetchLastDataRefreshTableForDownload();
        break;
      case "tech-awareness":
        // Tech Awareness only recognizes a EUR-market subset of the full market list (whatever
        // techAwarenessFilters.market - its own /eur-epwr-awareness/filters response - actually
        // contains). Switching here from another page whose market isn't in that list (e.g.
        // "United States") used to fire both of these refresh-date/available-range calls with
        // that still-wrong market immediately, before BrandPerformanceFilters.tsx's own
        // correction effect (which reads this exact same techAwarenessFilters.market list) got a
        // chance to dispatch a market this page actually supports - hence "wrong market" calls
        // visible in the network tab right after switching tabs. Wait until the current market is
        // one Tech Awareness itself recognizes before calling either endpoint at all; once
        // BrandPerformanceFilters corrects it, `market` changing re-fires this effect.
        if (techAwarenessFilters?.market?.includes(market)) {
          refetchLastDataRefreshTechAwareness();
          refetchAvailableDataDateRangeTechAwareness();
        }
        break;
      default:
        refetchLastDataRefresh();
        refetchAvailableDataDateRange();
        break;
    }
  }, [performancePageParam, performancePage, market, data_category, techAwarenessFilters]);

  useEffect(() => {
    eventTracking(MixpanelEvents.page_view, { page: performancePage });
    dispatch(setSpecificParameter("performance_page", performancePage));
  }, [performancePage]);

  // fetch filter queries
  const {
    data: brandVolumeFilters,
    isFetching: IsLoadingBrandVolumeFilters,
    refetch: refetchBrandVolumeFilters,
  } = useFilterQuery<any, any>(
    ["brandVolumeFilters", chartGranularity, params, nissan_competitor],
    getBrandPerformanceVolumeFilters,
    params,
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call brand oaa filters
  const {
    data: brandOaOFilters,
    isFetching: IsLoadingBrandOaOFilters,
    refetch: refetchBrandOaOFilters,
  } = useFilterQuery<any, any>(
    ["brandOaOFilters", chartGranularity, params, nissan_competitor],
    getBrandPerformanceOaOFilters,
    params,
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call brand health index filters
  const {
    data: brandHealthIndexFilters,
    isFetching: isLoadingBrandHealthIndexFilters,
    refetch: refetchBrandHealthIndexFilters,
  } = useFilterQuery<any, any>(
    ["brandHealthIndexFilters", chartGranularity, params, nissan_competitor],
    getBrandHealthIndexFilters,
    params,
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call buzz tracking filters
  const {
    data: buzzTrackingFilters,
    isFetching: isLoadingBuzzTrackingFilters,
    refetch: refetchBuzzTrackingFilters,
  } = useFilterQuery<any, any>(
    ["buzzTrackingFilters", chartGranularity, params, nissan_competitor],
    getBuzzTrackingFilters,
    params,
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call brand image attribute filters
  const {
    data: brandImageAttributeFilters,
    isFetching: IsLoadingBrandImageAttributeFilters,
    refetch: refetchBrandImageAttributeFilters,
  } = useFilterQuery<any, any>(
    ["brandImageAttributeFilters", chartGranularity, params, nissan_competitor],
    getBrandPerformanceImageAttributesFilters,
    params,
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call brand segment volume filters
  const {
    data: brandSegmentVolumeFilters,
    isFetching: isLoadingBrandSegmentVolumeFilters,
    refetch: refetchBrandSegmentVolumeFilters,
  } = useFilterQuery<any, any>(
    ["brandSegmentVolumeFilters", chartGranularity, params, nissan_competitor],
    getBrandSegmentVolumeFilters,
    { ...params, granularity: chartGranularity },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call brand market share filters
  const {
    data: brandMarketShareFilters,
    isFetching: isLoadingBrandMarketShareFilters,
    refetch: refetchBrandMarketShareFilters,
  } = useFilterQuery<any, any>(
    ["brandMarketShareFilters", chartGranularity, params, nissan_competitor],
    getBrandMarketShareFilters,
    params,
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call sales by volume filters
  const {
    data: marketShareByChannelFilters,
    isFetching: isLoadingMarketShareByChannelFilters,
    refetch: refetchMarketShareByChannelFilters,
  } = useFilterQuery<any, any>(
    ["marketShareByChannelFilters", chartGranularity, params, nissan_competitor],
    getMarketShareByChannelFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call brand kpi trend filters
  const {
    data: brandKPITrendFilters,
    isFetching: isLoadingBrandKPITrendFilters,
    refetch: refetchBrandKPITrendFilters,
  } = useFilterQuery<any, any>(
    ["brandKPITrendFilters", chartGranularity, params, nissan_competitor],
    getBrandKPITrendFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call brand kpi summary filters
  const {
    data: brandKPISummaryFilters,
    isFetching: isLoadingBrandKPISummaryFilters,
    refetch: refetchBrandKPISummaryFilters,
  } = useFilterQuery<any, any>(
    ["brandKPISummaryFilters", chartGranularity, params, nissan_competitor],
    getBrandKPISummaryFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call sov/som filters
  const {
    data: brandPerformanceSovSomFilters,
    isFetching: isLoadingBrandPerformanceSovSomFilters,
    refetch: refetchBrandPerformanceSovSomFilters,
  } = useFilterQuery<any, any>(
    ["brandPerformanceSovSomFilters", chartGranularity, params, nissan_competitor],
    getBrandPerformanceSovSomFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call purchase funnel filters
  const {
    data: purchaseFunnelFilters,
    isFetching: isLoadingPurchaseFunnelFilters,
    refetch: refetchPurchaseFunnelFilters,
  } = useFilterQuery<any, any>(
    // `competitor` is deliberately left out of this key. This same query's response is used
    // elsewhere to auto-correct `competitor` if the current selection is no longer valid (see
    // BrandPerformanceFilters.tsx). If `competitor` were part of the key, that correction would
    // change the key while the fetch was still in flight, and the response would land under a
    // key nobody is reading anymore - so the correction never applies and the UI stays stuck on
    // an old competitor value.
    ["purchaseFunnelFilters", chartGranularity, { ...params, competitor: undefined }, nissan_competitor],
    getPurchaseFunnelFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call image snapshot filters
  const {
    data: imageSnapshotFilters,
    isFetching: isFetchingImageSnapshotFilters,
    refetch: refetchImageSnapshotFilters,
    isLoading: isLoadingImageSnapshotFilters,
  } = useFilterQuery<any, any>(
    ["imageSnapshotFilters", chartGranularity, params, nissan_competitor],
    getImageSnapshotFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call table for download filters
  const {
    data: tableForDownloadMonthlyFilters,
    isFetching: isLoadingTableForDownloadMonthly,
    refetch: refetchTableForDownloadMonthly,
  } = useFilterQuery<any, any>(
    ["tableForDownloadMonthly", chartGranularity, params, nissan_competitor],
    getMonthlyTableForDownloadFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  const {
    data: tableForDownloadYearlyFilters,
    isFetching: isLoadingTableForDownloadYearly,
    refetch: refetchTableForDownloadYearly,
  } = useFilterQuery<any, any>(
    ["tableForDownloadYearly", chartGranularity, params, nissan_competitor],
    getYearlyTableForDownloadFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  const {
    data: tableForDownloadQuarterlyFilters,
    isFetching: isLoadingTableForDownloadQuarterly,
    refetch: refetchTableForDownloadQuarterly,
  } = useFilterQuery<any, any>(
    ["tableForDownloadQuarterly", chartGranularity, params, nissan_competitor],
    getQuarterlyTableForDownloadFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call nim filters

  const {
    data: nimFilters,
    isFetching: isLoadingNIMFilters,
    refetch: refetchNIMFilters,
  } = useFilterQuery<any, any>(
    ["nimFilters", chartGranularity, params, nissan_competitor],
    getNIMFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call nim latest filters
  const {
    data: nimLatestFilters,
    isFetching: isLoadingNIMLatestFilters,
    refetch: refetchNIMLatestFilters,
  } = useFilterQuery<any, any>(
    ["nimLatestFilters", chartGranularity, params, nissan_competitor],
    getNIMLatestFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call rv filters
  const {
    data: rvFilters,
    isFetching: isLoadingRVFilters,
    refetch: refetchRVFilters,
  } = useFilterQuery<any, any>(
    ["rvFilters", chartGranularity, params, nissan_competitor],
    getRVFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call table for download RV filters
  const {
    data: tableForDownloadRVFilters,
    isFetching: isLoadingTableForDownloadRV,
    refetch: refetchTableForDownloadRV,
  } = useFilterQuery<any, any>(
    ["tableForDownloadRV", chartGranularity, params, nissan_competitor],
    getRVTableForDownloadFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call tpva filters
  const {
    data: tpvaFilters,
    isFetching: isLoadingTPVAFilters,
    refetch: refetchTPVAFilters,
  } = useFilterQuery<any, any>(
    ["tpvaFilters", chartGranularity, params, nissan_competitor],
    getBrandPerformanceTPVAFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // call tp filters
  const {
    data: tpFilters,
    isFetching: isLoadingTPFilters,
    refetch: refetchTPFilters,
  } = useFilterQuery<any, any>(
    ["tpFilters", chartGranularity, params, nissan_competitor],
    getBrandPerformanceTPFilters,
    { ...params, performance_page: performancePage },
    onSettleCount,
    setOnSettleCount,
    setShouldRenderDataComponents
  );

  // set the filters in the redux store once the data is fetched
  useEffect(() => {
    switch (performancePage) {
      case "volume":
        if (brandVolumeFilters) {
          dispatch(setBrandPerformanceVolumeFilters(brandVolumeFilters));
        }
        break;

      case "oao":
        if (brandOaOFilters) {
          dispatch(setBrandPerformanceOaOFilters(brandOaOFilters));
        }
        break;

      case "brand-health-index":
        if (brandHealthIndexFilters) {
          dispatch(setBrandHealthIndexFilters(brandHealthIndexFilters));
        }
        break;

      case "buzz-tracking":
        if (buzzTrackingFilters) {
          dispatch(setBuzzTrackingFilters(buzzTrackingFilters));
        }
        break;

      case "image-attributes":
        if (brandImageAttributeFilters) {
          dispatch(setBrandPerformanceImageAttributeFilters(brandImageAttributeFilters));
        }
        break;

      case "segment-volume-tiv":
        if (brandSegmentVolumeFilters) {
          dispatch(setBrandSegmentVolumeFilters(brandSegmentVolumeFilters));
        }
        break;

      case "brand-market-share":
        if (brandMarketShareFilters) {
          dispatch(setBrandMarketShareVolumeFilters(brandMarketShareFilters));
        }
        break;

      case "market-share-by-channel":
        if (marketShareByChannelFilters) {
          dispatch(setSalesByVolumeFilters(marketShareByChannelFilters));
        }
        break;

      case "tp":
        if (tpFilters) {
          dispatch(setBrandPerformanceTPFilters(tpFilters));
        }
        break;

      case "tpva":
        if (tpvaFilters) {
          dispatch(setBrandPerformanceTPVAFilters(tpvaFilters));
        }
        break;

      case "brand-kpi-trend":
        if (brandKPITrendFilters) {
          dispatch(setBrandKPITrendFilters(brandKPITrendFilters));
        }
        break;
      case "brand-kpi-summary":
        if (brandKPISummaryFilters) {
          dispatch(setBrandKPISummaryFilters(brandKPISummaryFilters));
        }
        break;

      case "nim":
        if (nimFilters) {
          dispatch(setBrandPerformanceNIMFilters(nimFilters));
        }
        break;

      case "nim-latest":
        if (nimLatestFilters) {
          const maxRank = nimLatestFilters?.ranking?.length || 11;
          nimLatestFilters?.ranking.length && setNimLatestRanking(nimLatestFilters?.ranking[maxRank - 1]);
          dispatch(setBrandPerformanceNIMLatestFilters(nimLatestFilters));
        }
        break;

      case "sov/som":
        if (brandPerformanceSovSomFilters) {
          dispatch(setBrandPerformanceSovSomFilters(brandPerformanceSovSomFilters));
        }
        break;

      case "rv":
        if (rvFilters) {
          dispatch(setBrandPerformanceRVFilters(rvFilters));
        }
        break;

      case "purchase-funnel":
        if (purchaseFunnelFilters) {
          dispatch(setBrandPerformancePurchaseFunnelFilters(purchaseFunnelFilters));
        }
        break;

      case "tech-awareness":
        if (techAwarenessFilters) {
          dispatch(setBrandPerformanceTechAwarenessFilters(techAwarenessFilters));
        }
        break;

      case "image-snapshot":
        if (imageSnapshotFilters) {
          dispatch(setBrandPerformanceImageSnapshotFilters(imageSnapshotFilters));
        }
        break;
      case "monthly-table-for-download":
        if (tableForDownloadMonthlyFilters) {
          dispatch(SetMonthlyTableforDownloadFilters(tableForDownloadMonthlyFilters));
        }
        break;

      case "quarterly-table-for-download":
        if (tableForDownloadQuarterlyFilters) {
          dispatch(setQuarterlyTableForDownloadFilters(tableForDownloadQuarterlyFilters));
        }
        break;

      case "yearly-table-for-download":
        if (tableForDownloadYearlyFilters) {
          dispatch(setYearlyTableForDownloadFilters(tableForDownloadYearlyFilters));
        }
        break;

      case "rv-table-for-download":
        if (tableForDownloadRVFilters) {
          dispatch(setBrandPerformanceRVFilters(tableForDownloadRVFilters));
        }
        break;

      default:
        dispatch(setParameters(DEFAULT_BRAND_PERFORMANCE_PARAMETERS));
        break;
    }
  }, [
    brandVolumeFilters,
    brandOaOFilters,
    brandHealthIndexFilters,
    buzzTrackingFilters,
    brandImageAttributeFilters,
    brandSegmentVolumeFilters,
    brandMarketShareFilters,
    marketShareByChannelFilters,
    brandKPITrendFilters,
    brandKPISummaryFilters,
    brandPerformanceSovSomFilters,
    purchaseFunnelFilters,
    techAwarenessFilters,
    nimFilters,
    performancePage,
    performancePageParam,
    imageSnapshotFilters,
    tableForDownloadMonthlyFilters,
    tableForDownloadYearlyFilters,
    tableForDownloadQuarterlyFilters,
    nimLatestFilters,
    rvFilters,
    tableForDownloadRVFilters,
    tpvaFilters,
    tpFilters,
  ]);

  // when the page changes, call the appropriate filter query then set the filters in the redux store
  useEffect(() => {
    setOnSettleCount(0);
    setShouldRenderDataComponents(false);

    switch (performancePage) {
      case "volume":
        refetchBrandVolumeFilters();
        break;

      case "oao":
        refetchBrandOaOFilters();

        break;

      case "brand-health-index":
        refetchBrandHealthIndexFilters();
        break;

      case "buzz-tracking":
        refetchBuzzTrackingFilters();
        break;

      case "image-attributes":
        refetchBrandImageAttributeFilters();
        break;

      case "segment-volume-tiv":
        refetchBrandSegmentVolumeFilters();
        break;

      case "brand-market-share":
        refetchBrandMarketShareFilters();
        break;

      case "market-share-by-channel":
        refetchMarketShareByChannelFilters();
        break;

      case "brand-kpi-trend":
        refetchBrandKPITrendFilters();
        break;

      case "brand-kpi-summary":
        refetchBrandKPISummaryFilters();
        break;

      case "sov/som":
        refetchBrandPerformanceSovSomFilters();
        break;

      case "nim":
        refetchNIMFilters();
        break;

      case "nim-latest":
        refetchNIMLatestFilters();
        break;

      case "purchase-funnel":
        refetchPurchaseFunnelFilters();
        break;

      case "tech-awareness":
        refetchTechAwarenessFilters();
        break;

      case "image-snapshot":
        refetchImageSnapshotFilters();
        break;

      case "rv":
        refetchRVFilters();
        break;

      case "monthly-table-for-download":
        refetchTableForDownloadMonthly();
        break;

      case "yearly-table-for-download":
        refetchTableForDownloadYearly();
        break;

      case "quarterly-table-for-download":
        refetchTableForDownloadQuarterly();
        break;

      case "rv-table-for-download":
        refetchTableForDownloadRV();
        break;

      case "tpva":
        refetchTPVAFilters();
        break;

      case "tp":
        refetchTPFilters();
        break;

      default:
        break;
    }
  }, [
    performancePageParam,
    nissanCompetitorParam,
    brand,
    competitor,
    category,
    sub_category,
    model,
    market,
    attribute,
    date_range_brand_perf,
    granularity,
    date_range,
    brand_kpi,
    performancePage,
    nissan_competitor,
    segment,
    data_category,
    ranking,
    chartGranularity,
    select_value,
    dataSourceParam,
  ]);

  useEffect(() => {
    dispatch(setSpecificParameter("granularity", chartGranularity));
  }, [chartGranularity]);

  useEffect(() => {
    if (!granularitiesList?.includes(granularity)) {
      dispatch(setSpecificParameter("granularity", granularitiesList[0]));
      setChartGranularity(granularitiesList[0]);
    }
  }, [granularitiesList, granularity, date_range, chartGranularity]);

  return (
    <DashboardLayout>
      <DefaultPageContent
        filter={
          <BrandPerformanceFilters
            granularityOptions={
              <UsedCarsGranularityOptions
                onHandleRadioClick={handleGranularityRadioChange}
                granularity={chartGranularity}
                name={"brand_perf_granularities"}
                dateValue={date_range}
                granularitiesList={granularitiesList}
              />
            }
            activePage={performancePage}
            lastRefreshDate={latestDataRefresh}
            brandKPIMaxRangeScore={kpiTrendMaxScoreRange}
            setBrandKPIMaxRangeScore={setKPITrendMaxScoreRange}
            nimLatestRanking={nimLatestRanking}
            setNimLatestRanking={setNimLatestRanking}
            availableDataDateRange={availableDataDateRange}
            availableGranularities={granularitiesList}
          />
        }
        dataDocumentation="brand_performance"
        lastDataRefresh={latestDataRefresh}
        dataAvailableTerm={`${availableDataDateRange.min_date} - ${availableDataDateRange.max_date}`}
        pageTitle={
          <PageTitle
            dataDocumentation="brand_performance"
            tooltipText={performancePage in pageTooltipText ? pageTooltipText[performancePage] : ""}
            hideDataDocumentationButton={PERFORMANCE_DATA_EXCLUSION.includes(performancePage)}
          />
        }
        dashboardKey="brand_performance"
        brandPerformancePage={customCSVExport[`${performancePage}`]?.page}
        brandPerformanceExport={customCSVExport[`${performancePage}`]?.export}
        brandPerformanceCustomCSVUrl={customCSVUrl[`${performancePage}`]}
      >
        <div className="brand_performance_sotu">
          <div className="perf_dashboard">
            <PerfNavButtons
              setPerformancePage={setPerformancePage}
              activePage={performancePage}
              pagesWithData={PAGES_WITH_DATA_AVAILABLE}
            />
            <div className="perf_pages">
              {PAGES_WITH_DATA_AVAILABLE.includes(performancePage) ? (
                shouldRenderDataComponents ? (
                  performancePage == "volume" && !IsLoadingBrandVolumeFilters ? (
                    <BrandVolume
                      diff={diff}
                      activePage={performancePage}
                      onHandleRadioClick={handleRadioChange}
                      nissanCompetitor={nissan_competitor}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "oao" && !IsLoadingBrandOaOFilters ? (
                    <BrandOaO
                      diff={diff}
                      activePage={performancePage}
                      onHandleRadioClick={handleRadioChange}
                      nissanCompetitor={nissan_competitor}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "brand-health-index" && !isLoadingBrandHealthIndexFilters ? (
                    <BrandHealthIndex
                      diff={diff}
                      activePage={performancePage}
                      onHandleRadioClick={handleRadioChange}
                      nissanCompetitor={nissan_competitor}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "buzz-tracking" && !isLoadingBuzzTrackingFilters ? (
                    <BuzzTracking
                      diff={diff}
                      activePage={performancePage}
                      onHandleRadioClick={handleRadioChange}
                      nissanCompetitor={nissan_competitor}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "image-attributes" && !IsLoadingBrandImageAttributeFilters ? (
                    <BrandImageAttributes
                      diff={diff}
                      activePage={performancePage}
                      onHandleRadioClick={handleRadioChange}
                      nissanCompetitor={nissan_competitor}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "segment-volume-tiv" && !isLoadingBrandSegmentVolumeFilters ? (
                    <SegmentVolume diff={diff} activePage={performancePage} granularity={chartGranularity} />
                  ) : performancePage == "brand-market-share" && !isLoadingBrandMarketShareFilters ? (
                    <BrandMarketContent
                      diff={diff}
                      activePage={performancePage}
                      onHandleRadioClick={handleRadioChange}
                      nissanCompetitor={nissan_competitor}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "market-share-by-channel" && !isLoadingMarketShareByChannelFilters ? (
                    <ShareByChannelContent
                      diff={diff}
                      activePage={performancePage}
                      onHandleRadioClick={handleRadioChange}
                      nissanCompetitor={nissan_competitor}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "tp" && !isLoadingTPFilters ? (
                    <TPContent diff={diff} granularity={chartGranularity} />
                  ) : performancePage == "tpva" && !isLoadingTPVAFilters ? (
                    <TPVAContent diff={diff} granularity={chartGranularity} />
                  ) : performancePage == "tech-awareness" && !isLoadingTechAwarenessFilters ? (
                    <TechAwarenessContent diff={diff} granularity={chartGranularity} />
                  ) : performancePage == "brand-kpi-trend" && !isLoadingBrandKPITrendFilters ? (
                    <BrandKPIContent
                      diff={diff}
                      activePage={performancePage}
                      onHandleRadioClick={handleRadioChange}
                      nissanCompetitor={nissan_competitor}
                      maxScoreRange={kpiTrendMaxScoreRange}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "brand-kpi-summary" && !isLoadingBrandKPISummaryFilters ? (
                    <BrandKPISummaryContent
                      diff={diff}
                      activePage={performancePage}
                      onHandleRadioClick={handleRadioChange}
                      nissanCompetitor={nissan_competitor}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "purchase-funnel" && !isLoadingPurchaseFunnelFilters ? (
                    <PurchaseFunnelContent diff={diff} granularity={chartGranularity} />
                  ) : performancePage == "nim" && !isLoadingNIMFilters ? (
                    <NIMContent
                      diff={diff}
                      nissanCompetitor={nissan_competitor}
                      onHandleRadioClick={handleRadioChange}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "sov/som" && !isLoadingBrandPerformanceSovSomFilters ? (
                    <SovSomContent
                      diff={diff}
                      nissanCompetitor={nissan_competitor}
                      onHandleRadioClick={handleRadioChange}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "rv" && !isLoadingRVFilters ? (
                    <RvContent diff={diff} granularity={chartGranularity} />
                  ) : performancePage == "image-snapshot" && !isLoadingImageSnapshotFilters ? (
                    <ImageSnapshotContent
                      diff={diff}
                      nissanCompetitor={nissan_competitor}
                      onHandleRadioClick={handleRadioChange}
                      isFetchingFilter={isFetchingImageSnapshotFilters}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "nim-latest" && !isLoadingNIMLatestFilters ? (
                    <NIMLatestContent
                      diff={diff}
                      nissanCompetitor={nissan_competitor}
                      onHandleRadioClick={handleRadioChange}
                      nimLatestRanking={nimLatestRanking}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "monthly-table-for-download" && !isLoadingTableForDownloadMonthly ? (
                    <TableForDownloadMonthly
                      diff={diff}
                      nissanCompetitor={nissan_competitor}
                      onHandleRadioClick={handleRadioChange}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "quarterly-table-for-download" && !isLoadingTableForDownloadQuarterly ? (
                    <TableForDownloadQuarterly
                      diff={diff}
                      nissanCompetitor={nissan_competitor}
                      onHandleRadioClick={handleRadioChange}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "yearly-table-for-download" && !isLoadingTableForDownloadYearly ? (
                    <TableForDownloadYearly
                      diff={diff}
                      nissanCompetitor={nissan_competitor}
                      onHandleRadioClick={handleRadioChange}
                      granularity={chartGranularity}
                    />
                  ) : performancePage == "rv-table-for-download" && !isLoadingTableForDownloadRV ? (
                    <TableForDownloadRV
                      diff={diff}
                      nissanCompetitor={nissan_competitor}
                      onHandleRadioClick={handleRadioChange}
                      granularity={chartGranularity}
                    />
                  ) : (
                    <LoadingEllipsis isLoading={true} />
                  )
                ) : (
                  <LoadingEllipsis isLoading={true} />
                )
              ) : (
                <div className="data_not_found_area primary_tile_background">
                  This dashboard page is in development and will be available soon in CEDAR home
                </div>
              )}
            </div>
          </div>
        </div>
      </DefaultPageContent>
    </DashboardLayout>
  );
});

export default BrandPerformanceDashboard;
